import React from 'react';
import { useTranslation } from 'react-i18next';
import {
    Accordion,
    AccordionDetails,
    AccordionSummary,
    Box,
    Chip,
    IconButton,
    LinearProgress,
    Tooltip,
    Typography,
} from '@mui/material';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import CloseIcon from '@mui/icons-material/Close';
import CloudDownloadIcon from '@mui/icons-material/CloudDownload';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import HourglassEmptyOutlinedIcon from '@mui/icons-material/HourglassEmptyOutlined';
import ReplayIcon from '@mui/icons-material/Replay';
import { alpha } from '@mui/material/styles';
import { SCUIButton } from '../../atoms';
import {
    DownloadItemStatus,
    DownloadQueueItem,
    formatDownloadDuration,
    getDownloadChipColor,
    getDownloadStatusTextKey,
} from './SCUIS3Browser.helpers';

type S3BrowserDownloadPanelProps = {
    downloadItems: DownloadQueueItem[];
    downloadStatusExpanded: boolean;
    onExpandedChange: (expanded: boolean) => void;
    downloadCompletedCount: number;
    downloadFailedCount: number;
    downloading: boolean;
    downloadElapsedMs: number;
    lastDownloadDurationMs: number | null;
    onClear: () => void;
    onRetryFailed: () => void;
};

export const S3BrowserDownloadPanel: React.FC<S3BrowserDownloadPanelProps> = ({
    downloadItems,
    downloadStatusExpanded,
    onExpandedChange,
    downloadCompletedCount,
    downloadFailedCount,
    downloading,
    downloadElapsedMs,
    lastDownloadDurationMs,
    onClear,
    onRetryFailed,
}) => {
    const { t } = useTranslation();

    return (
        <Box
            sx={{
                position: 'sticky',
                top: 0,
                zIndex: 20,
                mb: 2,
                flexShrink: 0,
            }}
        >
            <Accordion
                expanded={downloadStatusExpanded}
                onChange={(_, expanded) => onExpandedChange(expanded)}
                sx={{
                    borderRadius: 2,
                    border: '1px solid #e0e0e0',
                    boxShadow: '0 2px 10px rgba(0,0,0,0.08)',
                    '&:before': { display: 'none' },
                }}
            >
                <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, width: '100%', pr: 1 }}>
                        <CloudDownloadIcon color="primary" />
                        <Typography sx={{ fontWeight: 600, flex: 1 }}>{t('aiPrompt.s3Browser.downloadStatus')}</Typography>
                        <Chip
                            size="small"
                            icon={<CloudDownloadIcon />}
                            label={`${downloadCompletedCount}/${downloadItems.length}`}
                            color={downloadFailedCount > 0 ? 'warning' : 'primary'}
                            variant="outlined"
                        />
                        {(downloading || lastDownloadDurationMs !== null) && (
                            <Tooltip
                                title={
                                    downloading
                                        ? t('aiPrompt.s3Browser.downloadElapsed', {
                                              time: formatDownloadDuration(downloadElapsedMs),
                                          })
                                        : t('aiPrompt.s3Browser.downloadTotalDuration', {
                                              time: formatDownloadDuration(lastDownloadDurationMs!),
                                          })
                                }
                                arrow
                                placement="top"
                            >
                                <Chip
                                    icon={
                                        downloading ? (
                                            <HourglassEmptyOutlinedIcon sx={{ fontSize: 18, color: 'warning.main' }} />
                                        ) : (
                                            <AccessTimeIcon sx={{ fontSize: 18, color: 'success.main' }} />
                                        )
                                    }
                                    label={formatDownloadDuration(
                                        downloading ? downloadElapsedMs : lastDownloadDurationMs!
                                    )}
                                    size="small"
                                    sx={(theme) => ({
                                        fontWeight: 700,
                                        fontVariantNumeric: 'tabular-nums',
                                        bgcolor: downloading
                                            ? alpha(theme.palette.warning.main, 0.14)
                                            : alpha(theme.palette.success.main, 0.14),
                                        color: downloading ? theme.palette.warning.dark : theme.palette.success.dark,
                                        border: '1px solid',
                                        borderColor: downloading
                                            ? theme.palette.warning.light
                                            : theme.palette.success.light,
                                        '& .MuiChip-icon': { ml: '4px' },
                                    })}
                                />
                            </Tooltip>
                        )}
                        {!downloading && (
                            <IconButton
                                size="small"
                                aria-label={t('aiPrompt.s3Browser.downloadCloseStatus')}
                                onClick={(e) => {
                                    e.stopPropagation();
                                    onClear();
                                }}
                            >
                                <CloseIcon fontSize="small" />
                            </IconButton>
                        )}
                    </Box>
                </AccordionSummary>
                <AccordionDetails sx={{ maxHeight: 320, overflowY: 'auto', pt: 0 }}>
                    {downloadItems.map((item) => (
                        <Box
                            key={item.id}
                            sx={{
                                mb: 1.5,
                                p: 1.5,
                                border: '1px solid #e0e0e0',
                                borderRadius: 2,
                                backgroundColor: 'white',
                            }}
                        >
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                                <Typography
                                    variant="body2"
                                    sx={{ flex: 1, fontWeight: 600 }}
                                    noWrap
                                    title={item.fileName}
                                >
                                    {item.fileName}
                                </Typography>
                                <Chip
                                    size="small"
                                    label={`${t(getDownloadStatusTextKey(item.status))} (${item.progress}%)`}
                                    color={getDownloadChipColor(item.status)}
                                />
                            </Box>
                            <LinearProgress
                                variant="determinate"
                                value={item.progress}
                                color={item.status === DownloadItemStatus.Failed ? 'error' : 'primary'}
                            />
                            {item.error && (
                                <Typography variant="caption" color="error" sx={{ mt: 0.5, display: 'block' }}>
                                    {item.error}
                                </Typography>
                            )}
                        </Box>
                    ))}
                    {!downloading && downloadFailedCount > 0 && (
                        <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 1, mt: 1 }}>
                            <Typography variant="body2" color="text.secondary" sx={{ flex: 1, alignSelf: 'center' }}>
                                {t('aiPrompt.s3Browser.downloadPartialMessage', {
                                    failed: downloadFailedCount,
                                })}
                            </Typography>
                            <SCUIButton
                                type="primary"
                                sizeType="default"
                                size="s"
                                text={t('aiPrompt.s3Browser.downloadRetryFailed')}
                                onClick={onRetryFailed}
                                showStartIcon
                                startIcon={<ReplayIcon />}
                            />
                        </Box>
                    )}
                </AccordionDetails>
            </Accordion>
        </Box>
    );
};
